import React from "react";
import { Link } from "react-router-dom";
import { EVENT_LABELS } from "./activityLogLabels";

export function parseChangedData(raw: unknown): Record<string, any> | null {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return raw as Record<string, any>;
  if (typeof raw !== "string") return { summary: String(raw) };
  try {
    return JSON.parse(raw);
  } catch {
    return { summary: raw };
  }
}

function isSystemName(name?: string | null): boolean {
  const s = name != null ? String(name).trim() : "";
  if (!s) return true;
  return /^system(\b|\s|\(|$)/i.test(s) || s.toUpperCase() === "SYSTEM";
}

export function displayActor(row: any): string {
  const raw =
    row.actorName ||
    row.actor?.userName ||
    [row.actor?.firstName, row.actor?.lastName].filter(Boolean).join(" ") ||
    row.actor?.email ||
    "";
  if (isSystemName(raw)) {
    return row.userId ? `User #${row.userId}` : "Automatic";
  }
  return String(raw);
}

export function displayRole(row: any): string {
  const r = row.userRole || row.actor?.userRole;
  if (!r || String(r).toUpperCase() === "SYSTEM") return "—";
  return String(r);
}

export function eventLabel(eventType?: string | null): string {
  if (!eventType) return "";
  return EVENT_LABELS[eventType] || eventType.replace(/_/g, " ").toLowerCase();
}

export function actionBadgeClass(action?: string): string {
  switch (action) {
    case "Add":
      return "bg-success";
    case "Delete":
      return "bg-danger";
    case "Edit":
      return "bg-primary";
    case "Login":
    case "Logout":
      return "bg-secondary";
    case "Assign":
    case "Access":
      return "bg-info";
    case "Publish":
      return "bg-warning text-dark";
    case "Close":
      return "bg-dark";
    case "Notify":
      return "bg-info";
    case "Lifecycle":
      return "bg-secondary";
    default:
      return "bg-light text-dark";
  }
}

function extraFacts(p: Record<string, any>): { label: string; value: string }[] {
  const summary = String(p.summary || "");
  const facts: { label: string; value: string }[] = [];
  const already = (value: string) =>
    Boolean(value) &&
    summary.toLowerCase().includes(String(value).toLowerCase());
  const push = (label: string, value?: string | null) => {
    const v = value != null ? String(value).trim() : "";
    if (!v || already(v)) return;
    facts.push({ label, value: v });
  };

  const retreat =
    p.retreatLabel ||
    (p.retreatTitle
      ? `${p.retreatTitle}${p.retreatId != null ? ` (Retreat ID ${p.retreatId})` : ""}`
      : p.retreatId != null
        ? `Retreat ID ${p.retreatId}`
        : "");

  let couple = String(
    p.coupleName || p.next?.coupleName || p.previous?.coupleName || "",
  ).trim();
  if (!couple && (p.record === "Couple" || p.primaryKey != null) && p.name) {
    couple = String(p.name);
  }
  if (
    couple &&
    p.primaryKey != null &&
    !String(couple).includes(`(${p.primaryKey})`)
  ) {
    couple = `${couple} (${p.primaryKey})`;
  }

  const priest = String(
    p.priestName || (p.record === "Priest" ? p.name : "") || "",
  ).trim();
  const parish = String(
    p.parishName ||
      p.parishLabel ||
      (p.record === "Parish" ? p.name : "") ||
      "",
  ).trim();
  const role = String(
    p.record === "Role" ? p.name || "" : p.role || "",
  ).trim();
  const ticket = String(
    p.ticketSubject
      ? p.recordId != null
        ? `${p.ticketSubject} (ID ${p.recordId})`
        : p.ticketSubject
      : p.record === "Ticket"
        ? p.name || ""
        : "",
  ).trim();
  const service = String(p.record === "Service" ? p.name || "" : "").trim();
  const talk = String(p.record === "Talk" ? p.name || "" : "").trim();
  const category = String(
    p.record === "Parish category" ? p.name || "" : "",
  ).trim();

  push("Retreat", retreat);
  push("Couple", couple);
  push("Priest", priest);
  push("Parish", parish);
  push("Role", role);
  push("Ticket", ticket);
  push("Service", service);
  push("Talk", talk);
  push("Parish category", category);
  return facts;
}

export function activitySummaryText(
  raw?: unknown,
  parsed?: Record<string, any> | null,
): string {
  const p = parsed || parseChangedData(raw);
  if (!p) return "";
  const parts = [p.summary].filter(Boolean);
  for (const f of extraFacts(p)) {
    parts.push(`${f.label}: ${f.value}`);
  }
  if (parts.length) return parts.join(" · ");
  if (p.record) {
    const label = p.name || p.title || (p.recordId != null ? `#${p.recordId}` : "");
    return `${p.record}${label ? `: ${label}` : ""}`;
  }
  return typeof raw === "string" ? raw : "";
}

export function ChangedDataView({
  raw,
  parsed,
}: {
  raw?: unknown;
  parsed?: Record<string, any> | null;
}) {
  const p = parsed || parseChangedData(raw);
  if (!p) return <span>—</span>;

  const facts = extraFacts(p);
  const summary = p.summary ? String(p.summary) : "";

  const extras = facts.length ? (
    <div className="d-flex flex-column gap-0 mt-1">
      {facts.map((f) => (
        <div key={f.label} className="small text-muted">
          {f.label}: {f.value}
        </div>
      ))}
    </div>
  ) : null;

  if (Array.isArray(p.changes) && p.changes.length) {
    return (
      <div className="d-flex flex-column gap-1 py-1">
        {summary && <div className="small">{summary}</div>}
        {p.changes.map((c: any, idx: number) => {
          const oldVal =
            c.old && typeof c.old === "object"
              ? c.old.coupleName || JSON.stringify(c.old)
              : c.old;
          const newVal =
            c.new && typeof c.new === "object"
              ? c.new.coupleName || JSON.stringify(c.new)
              : c.new;
          return (
            <div key={idx} className="small">
              <span className="text-muted">{c.field || c.key}:</span>{" "}
              <span>{oldVal == null || oldVal === "" ? "—" : String(oldVal)}</span>
              <span className="mx-1 text-primary fw-bold">→</span>
              <span>{newVal == null || newVal === "" ? "—" : String(newVal)}</span>
            </div>
          );
        })}
        {extras}
      </div>
    );
  }

  if (p.success === true || p.success === false) {
    return (
      <div className="small">
        <span className={`badge ${p.success ? "bg-success" : "bg-danger"} me-1`}>
          {p.success ? "Success" : "Failure"}
        </span>
        {summary || "Notification sent"}
        {extras}
      </div>
    );
  }

  if (summary) {
    return (
      <div>
        <span title={summary}>{summary}</span>
        {extras}
      </div>
    );
  }

  if (p.record) {
    const label = p.name || p.title || (p.recordId != null ? `#${p.recordId}` : "");
    return (
      <div>
        <span>
          {p.record}
          {label ? `: ${label}` : ""}
        </span>
        {extras}
      </div>
    );
  }

  if (extras) return extras;
  return <span>—</span>;
}

export function ModuleLinkCell({ row }: { row: any }) {
  const link = row.moduleLink;
  if (!link) return <span className="text-muted">{row.module || "—"}</span>;
  const internal = link.startsWith("/");
  if (internal) {
    return (
      <Link to={link.split("#")[0]} title={link}>
        {row.module || link}
      </Link>
    );
  }
  return (
    <a href={link} target="_blank" rel="noreferrer">
      {row.module || link}
    </a>
  );
}
